import { observer } from "mobx-react";
import { store } from "Models/Store";
import { useEffect } from "react";
import { Button, Colors, Display, Sizes } from "../Button/Button";
import EntityList, { IEntityListHeaderProps } from "../EntityList/EntityList";
import TablePagination from "../Pagination/TablePagination";
import InlineSpinner from "../Spinner/InlineSpinner";
import ToggleSwitch from "../ToggleSwitch/ToggleSwitch";
import { useAppSettingStore } from "./SettingStores/AppSettingPageStore";
import { CreditProviderDto } from "./types/CreditProviderListStoreTypes";

export const CreditProviderList = observer(() => {
	const { creditProviderListStore: creditProviderSettingStore } = useAppSettingStore();

	const handleCreditProviderSummaryView = (creditProviderId: string) => {
		store.routerHistory.push(`/hub/settings/credit-providers/${creditProviderId}`);
	}

	const creditProviderColumns: IEntityListHeaderProps<CreditProviderDto>[] = [
		{
			displayName: "ID",
			columnName: "id",
			value: item => item.id,
			className: "field-id",
			hide: true,
		},
		{
			displayName: "Name",
			columnName: "name",
			value: item => item.name,
			className: "field-name",
		},
		{
			displayName: "Description",
			columnName: "description",
			value: creditProvider => creditProvider.description,
			className: "field-description",
		},
		{
			displayName: "Provider Type ID",
			columnName: "providerTypeId",
			value: creditProvider => creditProvider.providerTypeId,
			className: "field-providerTypeId",
			hide: true
		},
		{
			displayName: "Type",
			columnName: "type",
			value: creditProvider => creditProvider.providerTypeName,
			className: "field-type",
		},
		{
			displayName: "Active",
			columnName: "isActive",
			value: creditProvider => (
				<ToggleSwitch
					model={creditProvider}
					modelProperty="isActive"
					hideLabel
				/>),
			onClickValue: item => creditProviderSettingStore.setCreditProviderStatus(item.id, !item.isActive),
			className: "field-status",
		},
	];

	useEffect(() => {
		creditProviderSettingStore.reset();

		return () => {
			creditProviderSettingStore.clearFields();
		}
	}, [creditProviderSettingStore]);

	if (creditProviderSettingStore.hasError) {
		return (
			<div className="alert alert__error">
				<p>There was an error loading the credit providers. Please try again later.</p>
			</div>
		);
	}

	return (
		<>
			<div className="credit-provider-section">
				<div className="header">
					<h4>Available Credit Providers</h4>
					<Button
						className="add-credit-provider"
						sizes={Sizes.Medium}
						colors={Colors.Primary}
						display={Display.Outline}
						icon={{ icon: "plus-2", iconPos: "icon-left" }}
						onClick={() => store.routerHistory.push("/hub/settings/credit-providers/add")}
					>
						Add Credit Provider
					</Button>
				</div>
				{creditProviderSettingStore.isLoading && ( <InlineSpinner /> )}
				{creditProviderSettingStore.creditProviders.length === 0 && !creditProviderSettingStore.isLoading && (
					<div className="alert alert__info">
						<p>No credit providers found.</p>
					</div>
				)}
				{creditProviderSettingStore.creditProviders.length > 0 && (
					<div className="credit-provider-list">
						<div className="collection-component">
							<EntityList
								collection={creditProviderSettingStore.creditProviders}
								columns={creditProviderColumns}
								idColumn="id"
								sortColumn="name"
								sortDescending
								onClickRow={item => {
									handleCreditProviderSummaryView(item.id);
								}}
							/>
							{creditProviderSettingStore.totalCount > creditProviderSettingStore.pageSize && (
								<TablePagination
									perPage={creditProviderSettingStore.pageSize}
									pageNo={creditProviderSettingStore.pageNo - 1}
									totalRecords={creditProviderSettingStore.totalCount}
									onPageChange={pageNo => creditProviderSettingStore.setPageNo(pageNo + 1)}
								/>
							)}
						</div>
					</div>
				)}
			</div>
		</>
	);
});